import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Users2 } from "lucide-react";

const EMPTY = {
  agent_name: "", brokerage: "", phone: "", email: "", market_city: "",
  source: "", relationship_level: "", total_contacts: 0,
  last_contact_date: "", next_follow_up: "",
  deals_submitted: 0, deals_accepted: 0,
  agent_traps: "", trap_notes: "", active: true, notes: "",
};

const Stat = ({ label, value, tid }) => (
  <div data-testid={tid} className="hairline bg-[#0F0F0F] p-4">
    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">{label}</div>
    <div className="font-display text-2xl font-black tabular text-white">{value}</div>
  </div>
);

export default function AgentCRM() {
  const [data, setData] = useState({ stats: {}, agents: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/crm/agents");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...EMPTY, ...a }); setEditing(a.agent_id); setOpen(true); };

  const save = async () => {
    try {
      const payload = {
        ...form,
        total_contacts: Number(form.total_contacts) || 0,
        deals_submitted: Number(form.deals_submitted) || 0,
        deals_accepted: Number(form.deals_accepted) || 0,
      };
      if (editing) {
        await api.put(`/crm/agents/${editing}`, payload);
        toast.success("Agent updated");
      } else {
        await api.post("/crm/agents", payload);
        toast.success("Agent added");
      }
      setOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this agent?")) return;
    await api.delete(`/crm/agents/${id}`);
    toast.success("Deleted"); load();
  };

  const s = data.stats || {};

  return (
    <div className="p-8 md:p-10 max-w-[1500px]">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#39FF14] mb-2">// Agent CRM</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight">
            <Users2 className="inline mr-3 mb-2 text-[#39FF14]" size={40} />
            Your agent network.
          </h1>
        </div>
        <Button
          data-testid="add-agent-btn"
          onClick={openNew}
          className="rounded-none bg-[#39FF14] text-black hover:bg-white text-xs uppercase tracking-widest h-11 px-5"
        >
          <Plus size={14} className="mr-2" /> Add agent
        </Button>
      </div>

      {/* CRM Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <Stat tid="crm-total" label="Total Agents" value={s.total_agents ?? 0} />
        <Stat tid="crm-active" label="Active" value={s.active ?? 0} />
        <Stat tid="crm-partners" label="Partners" value={s.partners ?? 0} />
        <Stat tid="crm-submitted" label="Deals Submitted" value={s.deals_submitted ?? 0} />
        <Stat tid="crm-accepted" label="Deals Accepted" value={s.deals_accepted ?? 0} />
        <Stat tid="crm-conv" label="Conv. Rate" value={`${Math.round((s.conversion_rate || 0) * 100)}%`} />
        <Stat tid="crm-traps" label="Agents w/ Traps" value={s.agents_with_traps ?? 0} />
      </div>

      {/* Agents table */}
      <div className="hairline bg-[#0F0F0F] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-900">
              <th className="text-left px-4 py-3">Agent</th>
              <th className="text-left px-3 py-3">Brokerage</th>
              <th className="text-left px-3 py-3">Market</th>
              <th className="text-left px-3 py-3">Relationship</th>
              <th className="text-right px-3 py-3">Submitted</th>
              <th className="text-right px-3 py-3">Accepted</th>
              <th className="text-left px-3 py-3">Traps</th>
              <th className="text-left px-3 py-3">Next FU</th>
              <th className="text-left px-3 py-3">Status</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.agents.length === 0 && (
              <tr><td colSpan={10} className="text-center text-neutral-500 py-10 text-sm">
                No agents yet. Click <span className="text-[#39FF14]">Add agent</span> to start building your CRM.
              </td></tr>
            )}
            {data.agents.map((a) => (
              <tr key={a.agent_id} data-testid={`agent-row-${a.agent_id}`} className="border-b border-neutral-900 last:border-b-0 hover:bg-[#111]">
                <td className="px-4 py-3">
                  <div className="font-semibold">{a.agent_name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">{a.phone || a.email}</div>
                </td>
                <td className="px-3 py-3 text-neutral-300">{a.brokerage}</td>
                <td className="px-3 py-3 text-neutral-300">{a.market_city}</td>
                <td className="px-3 py-3">
                  {a.relationship_level && (
                    <span className={`hairline px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                      (a.relationship_level || "").toLowerCase() === "partner"
                        ? "text-[#39FF14] border-[#39FF14]" : "text-neutral-400"
                    }`}>{a.relationship_level}</span>
                  )}
                </td>
                <td className="px-3 py-3 text-right tabular">{a.deals_submitted || 0}</td>
                <td className="px-3 py-3 text-right tabular text-[#39FF14] font-bold">{a.deals_accepted || 0}</td>
                <td className="px-3 py-3 text-neutral-400 text-xs max-w-[180px] truncate">{a.agent_traps}</td>
                <td className="px-3 py-3 text-neutral-400 font-mono text-xs">{a.next_follow_up}</td>
                <td className="px-3 py-3">
                  {a.active ? (
                    <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">Active</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest text-neutral-500">Inactive</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`edit-${a.agent_id}`} onClick={() => openEdit(a)} className="text-neutral-400 hover:text-[#39FF14] mr-3">
                    <Pencil size={14} />
                  </button>
                  <button data-testid={`del-${a.agent_id}`} onClick={() => remove(a.agent_id)} className="text-neutral-400 hover:text-red-400">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="agent-dialog" className="rounded-none bg-[#0F0F0F] hairline max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-widest text-sm">
              {editing ? "Edit agent" : "New agent"}
            </DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              Track this agent's contact info, deal activity, and any traps you've set for them.
            </DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              ["agent_name", "Agent Name *"], ["brokerage", "Brokerage"],
              ["phone", "Phone"], ["email", "Email"],
              ["market_city", "Market / City"], ["source", "Source"],
              ["relationship_level", "Relationship Level (e.g. Partner, Lead, Prospect)"],
              ["last_contact_date", "Last Contact Date"],
              ["next_follow_up", "Next Follow-Up"],
              ["agent_traps", "Agent Traps"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5">{label}</label>
                <Input
                  data-testid={`field-${k}`}
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="rounded-none bg-[#0A0A0A] hairline"
                />
              </div>
            ))}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5">Total Contacts</label>
              <Input type="number" min="0" data-testid="field-total_contacts" value={form.total_contacts} onChange={(e) => setForm({ ...form, total_contacts: e.target.value })} className="rounded-none bg-[#0A0A0A] hairline tabular" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5">Deals Submitted</label>
              <Input type="number" min="0" data-testid="field-deals_submitted" value={form.deals_submitted} onChange={(e) => setForm({ ...form, deals_submitted: e.target.value })} className="rounded-none bg-[#0A0A0A] hairline tabular" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5">Deals Accepted</label>
              <Input type="number" min="0" data-testid="field-deals_accepted" value={form.deals_accepted} onChange={(e) => setForm({ ...form, deals_accepted: e.target.value })} className="rounded-none bg-[#0A0A0A] hairline tabular" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5">Trap Notes</label>
              <Textarea data-testid="field-trap_notes" value={form.trap_notes} onChange={(e) => setForm({ ...form, trap_notes: e.target.value })} className="rounded-none bg-[#0A0A0A] hairline min-h-[70px]" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5">Notes</label>
              <Textarea data-testid="field-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none bg-[#0A0A0A] hairline min-h-[70px]" />
            </div>
            <div className="md:col-span-2 flex items-center justify-between hairline bg-[#0A0A0A] p-3">
              <div className="text-[10px] uppercase tracking-widest text-neutral-400">Active?</div>
              <Switch data-testid="field-active" checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="cancel-agent-btn" variant="outline" onClick={() => setOpen(false)} className="rounded-none hairline bg-transparent">Cancel</Button>
            <Button data-testid="save-agent-btn" onClick={save} disabled={!form.agent_name} className="rounded-none bg-[#39FF14] text-black hover:bg-white uppercase tracking-widest text-xs">
              {editing ? "Save changes" : "Add agent"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
